import React, { useEffect, useState } from "react";
import { FeatherSparkles } from "@subframe/core";
import { Button } from "../../../../ui/components/Button";
import { Checkbox } from "../../../../ui/components/Checkbox";
import { Select } from "../../../../ui/components/Select";
import { TextArea } from "../../../../ui/components/TextArea";
import { TextField } from "../../../../ui/components/TextField";
import { TAX_NAME_MAX } from "./tdsTable";
import {
  createTdsInZoho,
  fetchTdsLedgers,
  suggestTdsLlmContext,
  updateTdsCuration,
  TDSRate,
  TdsLedgerSlot,
  TdsSectionType,
} from "../../../../services/api";
import { LedgerSlotSelect } from "./LedgerSlotSelect";
import { draftFor, emptyDraft, TdsRateDraft } from "./TdsRateDialog";

interface TdsRateFormProps {
  /** The rate being curated, or null to add one. */
  rate: TDSRate | null;
  /** The Act 2025 section catalogue, for the add form's picker. */
  sections: TdsSectionType[];
  /** notice for the panel, plus the Zoho tax id of a rate that was just created. */
  onSaved: (notice: string | null, createdTaxId: string | null) => void;
  onCancel: () => void;
}

/**
 * Add or curate one TDS rate, INSIDE the panel rather than over it.
 *
 * WHY THIS EXISTS ALONGSIDE TdsRateDialog. The dialog's reasoning was that a form
 * unfolding inside the table pushes every row below it out of position mid-scan.
 * That is true of a form that opens between the rows; this one opens at the foot
 * of the panel, under the divider the add button sits on, where there are no rows
 * below to move. What it buys is the thing a dialog cannot: the register stays on
 * screen while the form is filled, so "is there already a rate for 194C" is
 * answered by looking up rather than by cancelling.
 *
 * ONE AT A TIME PER PANEL, and the panel enforces it — opening Edit puts the add
 * form away and the other way round. Both take the whole panel width and both
 * write to the list above them, so two open at once would be two answers to the
 * same question.
 *
 * MOUNTED FRESH PER OPEN. The panel keys this component by the rate it is editing,
 * so switching rows remounts it rather than leaving the last row's typing in the
 * fields — which is what the dialog's seed-on-open bookkeeping existed to work
 * around.
 *
 * The field logic is the dialog's, unchanged: adding writes to the client's real
 * Zoho Books and mirrors locally; editing only touches the curated columns —
 * threshold, the deductee flags, the section label and the AI context — because
 * changing a rate already used on a bill makes Zoho expire it and mint a new one,
 * and reconciling that is the sync's job.
 */
export const TdsRateForm: React.FC<TdsRateFormProps> = ({
  rate,
  sections,
  onSaved,
  onCancel,
}) => {
  const isCreate = rate === null;
  const [draft, setDraft] = useState<TdsRateDraft>(() =>
    rate ? draftFor(rate) : emptyDraft(),
  );
  const [saving, setSaving] = useState(false);
  const [rewriting, setRewriting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const patch = (next: Partial<TdsRateDraft>) =>
    setDraft((current) => ({ ...current, ...next }));

  // Edit opens from a row that can be the whole table above where this renders,
  // so the form comes to the operator. Add does not scroll: its button is where
  // the form appears. block "start" rather than "center" because the form is
  // taller than half a viewport. master-admin's behaviour, and the reason its
  // inline forms do not feel like nothing happened.
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!isCreate) {
      rootRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [isCreate]);

  // The ledger slots. Only the add form shows them, and only it pays for them:
  // two live Zoho calls (chart of accounts + the rates that widen the offer).
  const [ledgers, setLedgers] = useState<{
    payable: TdsLedgerSlot;
    receivable: TdsLedgerSlot;
  } | null>(null);
  const [ledgerError, setLedgerError] = useState<string | null>(null);
  const [showLedgers, setShowLedgers] = useState(false);

  useEffect(() => {
    if (!isCreate) return;
    let live = true;
    fetchTdsLedgers()
      .then((result) => {
        if (live) setLedgers(result.data.slots);
      })
      .catch((err) => {
        if (live) {
          setLedgerError(
            err instanceof Error ? err.message : "Could not read accounts",
          );
        }
      });
    return () => {
      live = false;
    };
  }, [isCreate]);

  const ledgerName = (slot: TdsLedgerSlot | undefined, fallback: string) => {
    const id = slot?.default_account_id;
    if (!id) return fallback;
    return (
      slot?.accounts.find((a) => a.account_id === id)?.account_name ?? fallback
    );
  };

  /**
   * Clear a section's seeded values when the section changes underneath them.
   *
   * "Untouched" is measured against what the PREVIOUS section would have put
   * there, not against blank — otherwise picking a second section leaves the
   * first one's name and label behind, which is how a rate ends up filed under a
   * section it has nothing to do with.
   */
  const pickSection = (value: string) => {
    const picked = sections.find((s) => s.tax_specific_type === value) ?? null;
    const previous = sections.find(
      (s) => s.tax_specific_type === draft.tax_specific_type,
    );
    const untouched = (current: string, was: string | undefined) =>
      current.trim() === "" || current === was;

    patch({
      tax_specific_type: picked?.tax_specific_type ?? "",
      tax_name: untouched(draft.tax_name, previous?.description)
        ? picked?.description ?? ""
        : draft.tax_name,
      section: untouched(draft.section, previous?.section_no ?? undefined)
        ? picked?.section_no ?? ""
        : draft.section,
      llm_context: untouched(draft.llm_context, previous?.description)
        ? picked?.description ?? ""
        : draft.llm_context,
    });
  };

  const handleRewrite = async () => {
    setRewriting(true);
    setError(null);
    try {
      const result = await suggestTdsLlmContext(
        rate
          ? { id: rate.id, draft: draft.llm_context }
          : {
              draft: draft.llm_context,
              tax_name: draft.tax_name,
              tax_percentage: draft.tax_percentage,
              tax_specific_type: draft.tax_specific_type,
              section: draft.section,
            },
      );
      patch({ llm_context: result.data.suggestion });
    } catch (err) {
      setError(err instanceof Error ? err.message : "AI rewrite failed");
    } finally {
      setRewriting(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      if (isCreate) {
        const result = await createTdsInZoho({
          tax_name: draft.tax_name,
          tax_percentage: draft.tax_percentage,
          tax_specific_type: draft.tax_specific_type,
          start_date: draft.start_date,
          end_date: draft.end_date,
          tds_payable_account_id: draft.tds_payable_account_id,
          tax_account_id: draft.tax_account_id,
          section: draft.section,
          threshold: draft.threshold,
          business_display: draft.business_display,
          individual_display: draft.individual_display,
          llm_context: draft.llm_context,
        });
        onSaved(
          result.data.local_warning || "Rate created in Zoho Books and saved.",
          result.data.tds_tax_id || null,
        );
      } else {
        await updateTdsCuration(rate.id, {
          section: draft.section,
          threshold: draft.threshold,
          business_display: draft.business_display,
          individual_display: draft.individual_display,
          llm_context: draft.llm_context,
        });
        onSaved(null, null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save");
      setSaving(false);
    }
  };

  const canSave = isCreate
    ? draft.tax_name.trim() !== "" &&
      draft.tax_percentage.trim() !== "" &&
      draft.tax_specific_type !== ""
    : true;

  return (
    <div
      ref={rootRef}
      className="flex w-full flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-neutral-50 p-4"
    >
      <div className="flex flex-col items-start gap-1">
        <span className="text-body-bold font-body-bold text-default-font">
          {isCreate ? "New TDS rate" : rate.tds_tax_name}
        </span>
        <span className="text-caption font-caption text-subtext-color">
          {isCreate
            ? "Created in Zoho Books, then mirrored here."
            : `${rate.tds_tax_percentage}% · rate and name are managed in Zoho Books.`}
        </span>
      </div>

      {isCreate && (
        <div className="flex w-full flex-col items-start gap-4">
          {/* Act 2025 sections only. A rate created today applies under the Act
              in force; the 1961 vocabulary is retired, and its numbers still
              reach the table through the catalogue's old_section_no. */}
          <Select
            className="w-full"
            label="Section"
            placeholder="Select a section"
            helpText=""
            value={draft.tax_specific_type || ""}
            onValueChange={pickSection}
          >
            {sections.map((s) => (
              <Select.Item key={s.tax_specific_type} value={s.tax_specific_type}>
                {s.description} ({s.section_no})
              </Select.Item>
            ))}
          </Select>

          <div className="flex w-full flex-wrap items-start gap-4">
            <TextField
              className="grow"
              label="Tax name"
              helpText={
                // A counter on an empty field is noise; a counter at 44 of 50
                // is a warning.
                draft.tax_name.length > TAX_NAME_MAX - 10
                  ? `${draft.tax_name.length}/${TAX_NAME_MAX}`
                  : "what appears on the bill"
              }
            >
              <TextField.Input
                placeholder="e.g. Commission or Brokerage"
                maxLength={TAX_NAME_MAX}
                value={draft.tax_name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  patch({ tax_name: e.target.value })
                }
              />
            </TextField>
            <TextField className="w-32" label="Rate (%)" helpText="">
              <TextField.Input
                placeholder="2"
                value={draft.tax_percentage}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  patch({ tax_percentage: e.target.value })
                }
              />
            </TextField>
          </div>

          <div className="flex w-full flex-wrap items-start gap-4">
            <TextField className="grow" label="Applicable from" helpText="">
              <TextField.Input
                type="date"
                value={draft.start_date}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  patch({ start_date: e.target.value })
                }
              />
            </TextField>
            <TextField
              className="grow"
              label="Until"
              helpText="blank = open-ended"
            >
              <TextField.Input
                type="date"
                value={draft.end_date}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  patch({ end_date: e.target.value })
                }
              />
            </TextField>
          </div>

          {/* Zoho's own default, but named. Both slots stay unset unless somebody
              opens this, and unset means Zoho picks — which it does the same way
              every time, there being no setting that changes it. The per-rate
              override is its only lever. */}
          <div className="flex w-full flex-col items-start gap-3 rounded-md border border-solid border-neutral-border bg-default-background p-3">
            {!showLedgers ? (
              <span className="text-caption font-caption text-subtext-color">
                Tracked under{" "}
                <span className="text-caption-bold font-caption-bold text-default-font">
                  {ledgerName(ledgers?.payable, "TDS Payable")}
                </span>{" "}
                and{" "}
                <span className="text-caption-bold font-caption-bold text-default-font">
                  {ledgerName(ledgers?.receivable, "TDS Receivable")}
                </span>
                .{" "}
                {/* Not a hedge. An org with no TDS rates has neither account in
                    its chart yet, and Zoho makes both on the first one. */}
                {ledgers !== null &&
                  !ledgers.payable.default_account_id &&
                  !ledgers.receivable.default_account_id &&
                  "Zoho creates them if they are not there yet. "}
                <button
                  type="button"
                  className="text-caption-bold font-caption-bold text-brand-700 underline"
                  onClick={() => setShowLedgers(true)}
                >
                  Use different accounts
                </button>
              </span>
            ) : (
              <>
                <div className="flex w-full items-baseline justify-between gap-2">
                  <span className="text-caption-bold font-caption-bold text-default-font">
                    Accounts
                  </span>
                  <button
                    type="button"
                    className="text-caption font-caption text-brand-700 underline"
                    onClick={() => {
                      setShowLedgers(false);
                      patch({ tds_payable_account_id: "", tax_account_id: "" });
                    }}
                  >
                    Use Zoho's defaults
                  </button>
                </div>
                <div className="flex w-full flex-wrap items-start gap-4">
                  <LedgerSlotSelect
                    label="Payable"
                    slot="payable"
                    kind="tds"
                    accounts={ledgers?.payable.accounts ?? []}
                    value={draft.tds_payable_account_id}
                    onChange={(value) =>
                      patch({ tds_payable_account_id: value })
                    }
                    onSlotsRefreshed={setLedgers}
                  />
                  {/* tax_account_id is the RECEIVABLE on a TDS rate — it is the
                      payable on a TCS one. Zoho's naming, not ours. */}
                  <LedgerSlotSelect
                    label="Receivable"
                    slot="receivable"
                    kind="tds"
                    accounts={ledgers?.receivable.accounts ?? []}
                    value={draft.tax_account_id}
                    onChange={(value) => patch({ tax_account_id: value })}
                    onSlotsRefreshed={setLedgers}
                  />
                </div>
                {ledgerError && (
                  <span className="text-caption font-caption text-red-600">
                    {ledgerError} — leave these blank and Zoho will pick.
                  </span>
                )}
              </>
            )}
          </div>
        </div>
      )}

      <div className="flex w-full flex-wrap items-start gap-4">
        <TextField className="w-56" label="Section label" helpText="">
          <TextField.Input
            placeholder='e.g. "194J"'
            value={draft.section}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              patch({ section: e.target.value })
            }
          />
        </TextField>
        <TextField
          className="w-44"
          label="Threshold (₹)"
          helpText="Blank = always deduct"
        >
          <TextField.Input
            placeholder="e.g. 30000"
            value={draft.threshold}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              patch({ threshold: e.target.value })
            }
          />
        </TextField>
      </div>

      <div className="flex w-full flex-col items-start gap-2">
        <span className="text-body-bold font-body-bold text-default-font">
          Deductee
        </span>
        <div className="flex flex-wrap items-center gap-6">
          <div className="flex items-center gap-2">
            <Checkbox
              checked={draft.business_display}
              onCheckedChange={(c) => patch({ business_display: Boolean(c) })}
            />
            <span className="text-body font-body text-default-font">
              Companies
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              checked={draft.individual_display}
              onCheckedChange={(c) => patch({ individual_display: Boolean(c) })}
            />
            <span className="text-body font-body text-default-font">
              Individuals &amp; HUF
            </span>
          </div>
        </div>
      </div>

      <TextArea
        className="w-full"
        label="AI context"
        helpText="What the AI classifier reads to decide this rate fits a bill. Blank keeps it out of suggestions entirely."
      >
        <TextArea.Input
          className="min-h-[84px]"
          placeholder="e.g. Fees for professional services: legal, medical, consultancy… Rate 10%."
          value={draft.llm_context}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
            patch({ llm_context: e.target.value })
          }
        />
      </TextArea>

      {error && (
        <div className="flex w-full items-start rounded-lg border border-solid border-red-200 bg-red-50 p-3">
          <span className="text-body font-body text-red-600">{error}</span>
        </div>
      )}

      <div className="flex w-full flex-wrap items-center justify-between gap-2">
        <Button
          variant="neutral-secondary"
          size="small"
          icon={<FeatherSparkles />}
          onClick={handleRewrite}
          disabled={
            rewriting ||
            saving ||
            (isCreate &&
              draft.tax_name.trim() === "" &&
              draft.llm_context.trim() === "")
          }
        >
          {rewriting
            ? "Writing..."
            : draft.llm_context.trim()
              ? "Rewrite with AI"
              : "Write with AI"}
        </Button>
        <div className="flex items-center gap-2">
          <Button
            variant="neutral-tertiary"
            size="small"
            onClick={onCancel}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            variant="brand-primary"
            size="small"
            onClick={handleSave}
            disabled={saving || rewriting || !canSave}
          >
            {saving
              ? isCreate
                ? "Creating..."
                : "Saving..."
              : isCreate
                ? "Create in Zoho"
                : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TdsRateForm;
